<template>
  <div class="wizard-body small-9 columns">
    <page-header
      :header-title="$t('INBOX_MGMT.ADD.EVOLUTION_API.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.EVOLUTION_API.DESC')"
    />
    <form class="row" @submit.prevent="createEvolutionInstance">
      <div class="medium-8 columns">
        <label>
          {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.INBOX_NAME.LABEL') }}
          <woot-input-channel-name
            v-model.trim="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.EVOLUTION_API.INBOX_NAME.PLACEHOLDER')
            "
          />
        </label>
      </div>
      <div class="medium-8 columns">
        <label :class="{ error: $v.phoneNumber.$error || creationError }">
          {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.PHONE_NUMBER.LABEL') }}
          <input
            v-model.trim="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.EVOLUTION_API.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="$v.phoneNumber.$touch"
          />
          <span v-if="$v.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>

      <label class="medium-8 columns">
        {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.LABEL') }}
        <select v-model="allowSendMessages">
          <option :value="true">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.DISABLED') }}
          </option>
        </select>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.HELP_TEXT') }}
        </p>
      </label>

      <label class="medium-8 columns">
        {{
          $t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.LABEL'
          )
        }}
        <select v-model="enableDataExtraction">
          <option :value="true">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.ENABLED'
              )
            }}
          </option>
          <option :value="false">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.DISABLED'
              )
            }}
          </option>
        </select>
        <p class="help-text">
          {{
            $t(
              'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.HELP_TEXT'
            )
          }}
        </p>
      </label>
      <div class="medium-12 columns">
        <woot-submit-button
          :disabled="
            $v.inboxName.$invalid || $v.phoneNumber.$invalid || creationError
          "
          :button-text="$t('INBOX_MGMT.ADD.EVOLUTION_API.BUTTONS.LINK_BUTTON')"
          :loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<script>
import PageHeader from '../../../SettingsSubPageHeader';
import router from '../../../../../index';
import { required, maxLength } from 'vuelidate/lib/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';

export default {
  components: {
    PageHeader,
  },
  mixins: [alertMixin],
  data() {
    return {
      provider: 'evolution_api',
      title: 'WhatsApp vinculado',
      inboxName: '',
      phoneNumber: '',
      allowSendMessages: true,
      enableDataExtraction: false,
      qrCode: null,
      isCreating: false,
      creationError: false,
      inbox: {},
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  watch: {
    phoneNumber(oldVal, newVal) {
      this.creationError = false;
    },
  },
  validations: {
    inboxName: { required, maxLength: maxLength(30) },
    phoneNumber: { required, isPhoneE164OrEmpty },
  },
  methods: {
    // Normaliza el número de teléfono de WhatsApp para la API de Evolution
    // Reemplaza el prefijo +52 por +521
    // y asegura que el número tenga 10 dígitos
    // Ejemplo: +5212345678901 -> +5212345678901
    normalizedWhatsappPhoneNumber(phoneNumber) {
      return phoneNumber.replace(/^\+52(\d{10})$/, '+521$1');
    },
    // Construye los parámetros del canal para la creación de la bandeja de entrada de Evolution API
    channelParams() {
      let phoneNumber = this.normalizedWhatsappPhoneNumber(this.phoneNumber);

      return {
        allow_send_messages: this.allowSendMessages,
        enable_data_extraction: this.enableDataExtraction,
        inbox_name: this.inboxName,
        phone_number: phoneNumber,
        name: this.inboxName,
        channel: {
          phone_number: phoneNumber,
          type: 'evolutionapi',
        },
      };
    },
    // Crea una instancia de Evolution API y maneja la respuesta
    async createEvolutionInstance() {
      try {
        const response = await this.$store.dispatch(
          'inboxes/createEvolutionInstance',
          this.channelParams()
        );
        if (response.qrcode && response.qrcode.base64) {
          this.qrCode = response.qrcode.base64;
          this.inbox = response.inbox;
          this.nextStep();
        } else {
          this.creationError = true;
          this.showAlertError(
            this.$t('INBOX_MGMT.ADD.EVOLUTION_API.ERRORS.CREATION_FAILED')
          );
        }
      } catch (error) {
        this.creationError = true;
        this.showAlertError(
          this.$t('INBOX_MGMT.ADD.EVOLUTION_API.ERRORS.CREATION_FAILED')
        );
        console.error(
          this.$t('INBOX_MGMT.ADD.EVOLUTION_API.ERRORS.CREATION_FAILED'),
          error
        );
      }
    },
    nextStep() {
      const params = {
        sub_name: 'evolution_api',
        fields: {
          inboxName: this.inboxName,
          phoneNumber: this.normalizedWhatsappPhoneNumber(this.phoneNumber),
          allowSendMessages: this.allowSendMessages,
          qrCode: this.qrCode,
          inbox: this.inbox,
        },
      };
      return router.push({ name: 'settings_inbox_link_number', params });
    },
  },
};
</script>

<style scoped lang="scss"></style>
